import { prefetchInsights } from '../actions/publicActions';
import { getCalendarEventsOverlappingDateRanges } from 'owa-calendar-events-store/lib/utils/getCalendarEventsOverlappingDateRanges';
import { CalendarEvent } from 'owa-calendar-types';
import { startOfDay, today } from 'owa-datetime';
import { createDateRange } from 'owa-datetime-utils';
import { CACHE_SIZE, getInsightsTraceId, lazyFetchMeetingInsights } from 'owa-meeting-insights';
import {
    isInsightsEnabledForEvent,
    isInsightsEnabledForUser,
    InsightsDataFetchSource,
} from 'owa-meeting-insights-types-and-flights';
import { TaskQueue } from 'owa-task-queue';
import { orchestrator } from 'satcheljs';

interface InsightsPrefetchDataProps {
    event: CalendarEvent;
    dataFetchSource: InsightsDataFetchSource;
}

const MAX_NUM_GET_EVENT_RELATED_CONTENT: number = 3;
const insightsPrefetchTaskQueue = new TaskQueue<InsightsPrefetchDataProps>(
    MAX_NUM_GET_EVENT_RELATED_CONTENT,
    prefetchInsightsForMeeting,
    500 /* taskDelay */
);

export default orchestrator(prefetchInsights, actionMessage => {
    if (!isInsightsEnabledForUser()) {
        return;
    }

    const { events, dataFetchSource } = actionMessage;

    // prefetch today and tomorrow's events
    const todayAndTomorrowRange = createDateRange(startOfDay(today()), 3);
    const eventsToPrefetch = getCalendarEventsOverlappingDateRanges(events, [todayAndTomorrowRange])
        .eventsOverlapping;
    const maxNumToPrefetch = Math.min(eventsToPrefetch.length, CACHE_SIZE);
    for (let i = 0; i < maxNumToPrefetch; i++) {
        const event = eventsToPrefetch[i];

        // Add eventId to prefetch queue only for supported event type and client does not have server returned insights yet
        const shouldPrefetch =
            isInsightsEnabledForEvent(event.ItemId.Id) &&
            !getInsightsTraceId(event.ItemId.Id, dataFetchSource);
        if (shouldPrefetch) {
            insightsPrefetchTaskQueue.add({ event: event, dataFetchSource: dataFetchSource });
        }
    }
});

function prefetchInsightsForMeeting(dataProps: InsightsPrefetchDataProps): Promise<void> {
    const meetingId = dataProps.event.ItemId.Id;
    const dataFetchSource = dataProps.dataFetchSource;

    return lazyFetchMeetingInsights.importAndExecute(meetingId, dataFetchSource).catch(() => {
        /* The catch is to swallow the thrown error from fetchMeetingInsights. No need to handle the error as it is already logged inside fetchMeetingInsights.
         * fetchMeetingInsights has to return a rejected promise to tell if the task is completed or not, for the TaskQueue to schedule the next fetch task.
         */
    });
}
